import { useEffect, useRef } from "react";

/**
 * Ambient background constellation.
 * Canvas + rAF, ~40-60 nodes, distance-faded edges.
 * Cool-only palette (NEVER red — reserved for coverage/evidence).
 * Pauses on document.hidden and offscreen; skips if reduced motion.
 */
export default function Constellation({ density = 55 }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const mouseRef = useRef({ x: 0.5, y: 0.5 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let W = 0, H = 0, dpr = Math.min(window.devicePixelRatio || 1, 2);
    const nodes = [];
    // colors are read from CSS custom properties so theme changes are picked up on every rAF frame
    const readColors = () => {
      const s = getComputedStyle(document.documentElement);
      return {
        blue: s.getPropertyValue("--constel-blue").trim() || "rgba(96,165,250,0.65)",
        indigo: s.getPropertyValue("--constel-indigo").trim() || "rgba(99,102,241,0.55)",
        edge: s.getPropertyValue("--constel-edge").trim() || "rgba(96,165,250,0.22)",
      };
    };
    let COL = readColors();
    const themeObs = new MutationObserver(() => { COL = readColors(); });
    themeObs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });

    const resize = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (!w || !h) return false;
      const changed = Math.abs(w - W) > 1 || Math.abs(h - H) > 1;
      W = w; H = h;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      return changed;
    };

    const seed = () => {
      nodes.length = 0;
      for (let i = 0; i < density; i++) {
        nodes.push({
          x: Math.random() * W,
          y: Math.random() * H,
          vx: (Math.random() - 0.5) * 0.12,
          vy: (Math.random() - 0.5) * 0.12,
          r: 0.8 + Math.random() * 1.2,
          hue: Math.random() > 0.4 ? "blue" : "indigo",
        });
      }
    };

    const rescatter = () => {
      // Redistribute existing node positions across new dimensions (keeps velocity)
      if (!W || !H) return;
      if (nodes.length === 0) { seed(); return; }
      for (const n of nodes) {
        n.x = Math.random() * W;
        n.y = Math.random() * H;
      }
    };

    const onMouse = (e) => {
      mouseRef.current.x = e.clientX / window.innerWidth;
      mouseRef.current.y = e.clientY / window.innerHeight;
    };
    window.addEventListener("mousemove", onMouse, { passive: true });

    // Kick a resize + seed on next frame so hero layout is settled
    const kickoff = () => {
      resize();
      if (W && H) seed();
      else requestAnimationFrame(kickoff);
    };
    kickoff();

    // Observe container size — reseed when the hero grows/shrinks (initial mount,
    // theme toggle re-layout, viewport rotate, mobile keyboard, etc.)
    const ro = new ResizeObserver(() => {
      if (resize()) rescatter();
    });
    ro.observe(canvas);

    const onWinResize = () => { if (resize()) rescatter(); };
    window.addEventListener("resize", onWinResize);

    let last = 0;
    const FPS = 30;
    const step = (t) => {
      if (document.hidden) { rafRef.current = requestAnimationFrame(step); return; }
      if (t - last < 1000 / FPS) { rafRef.current = requestAnimationFrame(step); return; }
      last = t;

      ctx.clearRect(0, 0, W, H);
      const mx = (mouseRef.current.x - 0.5) * 6;
      const my = (mouseRef.current.y - 0.5) * 6;

      // edges
      const maxD = 150;
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const d = Math.hypot(dx, dy);
          if (d < maxD) {
            const alpha = (1 - d / maxD);
            // strip alpha from base and multiply
            ctx.strokeStyle = COL.edge.replace(/rgba?\(([^)]+)\)/, (m, inner) => {
              const parts = inner.split(",").map(s => s.trim());
              const base = parts.length === 4 ? parts.slice(0,3).join(",") + `,${(parseFloat(parts[3]) * alpha).toFixed(3)}` : parts.join(",") + `,${alpha.toFixed(3)}`;
              return `rgba(${base})`;
            });
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(a.x + mx, a.y + my);
            ctx.lineTo(b.x + mx, b.y + my);
            ctx.stroke();
          }
        }
      }
      // nodes
      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < -20) n.x = W + 20;
        if (n.x > W + 20) n.x = -20;
        if (n.y < -20) n.y = H + 20;
        if (n.y > H + 20) n.y = -20;
        ctx.beginPath();
        ctx.fillStyle = n.hue === "blue" ? COL.blue : COL.indigo;
        ctx.arc(n.x + mx, n.y + my, n.r, 0, Math.PI * 2);
        ctx.fill();
      }
      rafRef.current = requestAnimationFrame(step);
    };

    if (!reduced) rafRef.current = requestAnimationFrame(step);
    else {
      // static one-frame render
      ctx.fillStyle = "#0F1424";
      for (const n of nodes) { ctx.beginPath(); ctx.fillStyle = "rgba(96,165,250,0.5)"; ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2); ctx.fill(); }
    }

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("mousemove", onMouse);
      window.removeEventListener("resize", onWinResize);
      ro.disconnect();
      themeObs.disconnect();
    };
  }, [density]);

  return <canvas ref={canvasRef} className="hero-canvas" aria-hidden="true" />;
}
